/**
 * The (?) beside a plan setting: opens a short explanation of what the setting really does.
 *
 * A popover on click rather than a hover tooltip, because the panel is used on phones and a
 * tooltip can't be read without a mouse. It also says plainly when a setting is stored but not
 * enforced anywhere yet, so nobody sells a limit the app doesn't apply.
 */

import { useState } from 'react'
import { Chip, IconButton, Popover, Stack, Typography } from '@mui/material'
import { HelpOutline } from '@mui/icons-material'
import type { FieldHelpInfo } from './planSpecs'

const ENFORCEMENT_CHIP: Record<NonNullable<FieldHelpInfo['enforcement']>, { label: string; color: 'warning' | 'default' } | null> = {
  enforced: null,
  'display-only': { label: 'Shown only — not enforced', color: 'warning' },
  'not-used': { label: 'Not used by the app yet', color: 'default' },
}

export function FieldHelp({ label, info }: { label: string; info?: FieldHelpInfo }) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  if (!info) return null

  const chip = info.enforcement ? ENFORCEMENT_CHIP[info.enforcement] : null

  return (
    <>
      <IconButton
        size="small"
        aria-label={`About ${label}`}
        onClick={(e) => {
          // Inside a FormControlLabel a click would otherwise also flip the switch.
          e.preventDefault()
          e.stopPropagation()
          setAnchor(e.currentTarget)
        }}
        sx={{ color: 'text.secondary', flexShrink: 0 }}
      >
        <HelpOutline fontSize="small" />
      </IconButton>
      <Popover
        open={anchor !== null}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { p: 2, maxWidth: 'min(400px, calc(100vw - 32px))' } }}
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          <Typography variant="subtitle2">{label}</Typography>
          {chip && <Chip size="small" color={chip.color} label={chip.label} />}
        </Stack>
        <Stack spacing={1}>
          {info.body.map((paragraph) => (
            <Typography key={paragraph} variant="body2" color="text.secondary">
              {paragraph}
            </Typography>
          ))}
        </Stack>
      </Popover>
    </>
  )
}
